import axios from 'axios';
import { IS_DEVELOPMENT, HOSTNAME, USE_LOCAL_JSON } from '@constants';
import { auth } from '@modules/auth';

const devAuth = {
  username: 'pop',
  password: 'demo',
};
const REQUEST_TIMEOUT_MS = 8000;

let instance = null;

class Request {
  constructor() {
    if (!instance) {
      instance = this;
      this.isAlreadyFetchingAccessToken = false;
      this.subscribers = [];

      return instance;
    }
  }

  /**
   * Метод добавляет запрос в очередь
   * @param id - id запроса
   * @param callback - сам запрос
   * @private
   */
  _addSubscribe(id, callback) {
    this.subscribers.push({
      id,
      callback,
    });
  }

  /**
   * Метод удаляет запрос из очереди
   * @param id - id запроса
   * @private
   */
  _removeSubscribe(id) {
    this.subscribers = this.subscribers.filter((item) => item['id'] !== id);
  }

  /**
   * Метод вызывает запросы из очереди
   * @param access_token - новый access token
   * @private
   */
  _callSubscribers(access_token) {
    this.subscribers.map((request) => request.callback(access_token));
  }

  async call_json(path = '') {
    const host = IS_DEVELOPMENT
      ? USE_LOCAL_JSON
        ? 'http://localhost:3000'
        : 'http://planoplan.local'
      : `https://new.${HOSTNAME}`;
    const url = `${host}/json${path}`;

    return axios({
      method: 'get',
      url,
    });
  }

  async call_public(type, urls, params = {}, settings = {}) {
    const { method, data, url, responseType, reqHeaders } = this._setRequestParams(type, urls, params, settings);
    const headers = {
      'X-Requested-With': 'XMLHttpRequest',
    };

    Object.entries(reqHeaders).forEach(([key, value]) => {
      if (value) {
        headers[key] = value;
      }
    });

    return axios({
      method,
      data,
      url,
      responseType,
      headers,
      auth: devAuth, // !IS_PRODUCTION_HOST &&
      timeout: REQUEST_TIMEOUT_MS,
    });
  }

  async call_check_connection() {
    const headers = {
      'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    };

    const url = 'https://planoplan.com/check_connect';

    try {
      const res = await axios.get(url, {
        headers,
        params: { _: Date.now() },
        timeout: 3000,
        validateStatus: (s) => s >= 200 && s < 400,
      });
console.log(res.status);
      return res.status === 200;
    } catch {
      return false;
    }
  }

  async call_auth(type, urls, params = '', settings = {}) {
    const { locale, ctx } = settings;
    const payload = auth.getAuthData({ ctx });
    const RT = auth.getAuthRt({ ctx }); // Refresh token from SAT Auth

    if (!payload) {
      auth.logout({ locale, ctx });
    }

    const { access_token, refresh_token, expire } = payload || {};
    const id = Symbol('id');

    const retryOriginalRequest = new Promise((resolve) => {
      this._addSubscribe(id, (access_token) => resolve(request(access_token)));
    });

    const request = async (access_token) => {
      const headers = {
        'X-Requested-With': 'XMLHttpRequest',
        'Authorization': `Bearer ${access_token}`,
      };

      const { method, data, url, responseType } = this._setRequestParams(type, urls, params, settings);

      try {
        const request = await axios({
          method,
          url,
          data,
          responseType,
          headers,
          timeout: REQUEST_TIMEOUT_MS,
        });

        this._removeSubscribe(id);
        return request;
      } catch (error) {
        if (error.response && error.response.status === 400) {
          await this._refreshTokens(refresh_token, { locale, ctx });
          return retryOriginalRequest;
        }

        throw error;
      }
    };

    if (RT) {
      await this._refreshTokens(RT, { locale, ctx });

      return retryOriginalRequest;
    }

    if (!this._checkValidToken(expire)) {
      await this._refreshTokens(refresh_token, { locale, ctx });

      return retryOriginalRequest;
    }

    return request(access_token);
  }

  async _refreshTokens(refresh_token, { locale, ctx }) {
    if (this.isAlreadyFetchingAccessToken) {
      return;
    }

    this.isAlreadyFetchingAccessToken = true;

    try {
      const response = await axios({
        url: `https://api.${HOSTNAME}/v2/auth/token/`,
        method: 'post',
        data: {
          refresh_token,
          grant_type: 'refresh_token',
          scope: 'user',
        },
        auth: {
          username: 'standalone',
          password: 'GibLxP9EHoOt',
        },
        timeout: REQUEST_TIMEOUT_MS,
      });

      const { data } = response;
      const { access_token } = data;

      this._callSubscribers(access_token);

      auth.setAuthData(data, { ctx });
    } catch (error) {
      if (error.response && error.response.status === 400) {
        auth.logout({ locale, ctx });
      }

      throw error;
    } finally {
      this.isAlreadyFetchingAccessToken = false;
    }
  }

  _checkValidToken(expire) {
    return Number(expire) >= Math.floor(Date.now() / 1000);
  }

  _setRequestParams(type, urls, params, settings) {
    const { local, prod } = urls;
    const { responseType, useFormData = false, subDomain = '', headers = {} } = settings;

    const method = type === 'post' && USE_LOCAL_JSON ? 'get' : type;
    let withSubDomain = subDomain ? `${subDomain}.${HOSTNAME}` : HOSTNAME;
    if (HOSTNAME === 'test.sb.planoplan.ru') {
      withSubDomain = 'pp.ksdev.ru';
    }
    const path = USE_LOCAL_JSON ? `http://localhost:9999/${local}` : `https://${withSubDomain + prod}`;
    const url = useFormData ? path : path + setParams(params);
    const data = useFormData ? setFormData(params) : null;
    const reqHeaders = typeof headers === 'object' && headers !== null ? headers : {};

    return {
      method,
      data,
      url,
      responseType,
      reqHeaders,
    };
  }
}

const setParams = (params) => {
  let paramsString = '';
  let isFirst = true;

  const createParams = (i, item) => {
    if (!item) {
      return;
    }

    paramsString += isFirst ? '?' : '&';
    paramsString += i + '=' + item;

    isFirst = false;
  };

  for (let i in params) {
    if (typeof params[i] === 'object') {
      params[i].forEach((item) => {
        for (let j in item) {
          createParams(j, item[j]);
        }
      });
    } else {
      createParams(i, params[i]);
    }
  }

  return paramsString;
};

export const setFormData = (params) => {
  const formData = new FormData();

  for (const prop of Object.keys(params)) {
    formData.append(prop, params[prop]);
  }

  return formData;
};

export const request = new Request();
